import classNames from 'classnames';
import { MaterialIcons } from '@/common/components/shared/material-icons/MaterialIcons';
import { SkeletonLoader } from '@/common/components/shared/skeleton-loader/SkeletonLoader';
import type { ClientReportNumberOfEmployeeItem, ReportNumberOfEmployee, ReportNumberOfEmployeeItem } from './props';

interface TableRowProps<T extends ReportNumberOfEmployeeItem> {
  isCurrentColumn: (key: string) => boolean;
  depth: number;
  data: ReportNumberOfEmployee<T> | undefined;
  expandable: boolean | undefined;
  expandedRows: Array<string>;
  item: ClientReportNumberOfEmployeeItem;
  loading: boolean;
  yearMonths: Array<string>;
  onClickDimension: (id: string) => void;
}

export const TableRow = <T extends ReportNumberOfEmployeeItem>({
  isCurrentColumn,
  depth,
  data,
  expandable,
  expandedRows,
  item: { id, dimension, dimensionText, metrics, children },
  loading,
  yearMonths,
  onClickDimension,
}: TableRowProps<T>) => {
  const showChevron = expandable && depth < (data?.maxDepth ?? 1) - 1;
  const isLastDepth = depth === (data?.maxDepth ?? 1) - 1;
  const isOpen = (expandable ?? false) && expandedRows.some(it => it === id);

  return (
    <>
      <tr data-depth={depth} data-id={id}>
        <td
          className={classNames('!pl-4 bg-gray-0 lg:sticky lg:left-0', {
            'w-[200px]': loading,
            'min-w-[200px]': !loading,
            'max-w-[50dvw]': !loading,
            'md:max-w-[calc(50dvw-250px-24px)]': !loading,
          })}
        >
          <SkeletonLoader loading={loading}>
            <div
              className={classNames('inline-flex items-center gap-1 max-w-full overflow-hidden', {
                'cursor-pointer': showChevron,
              })}
              style={{ paddingLeft: depth * 16 + (expandable && isLastDepth ? 16 : 0) }}
              title={dimensionText}
              onClick={() => !isLastDepth && onClickDimension(id)}
            >
              {showChevron && (
                <MaterialIcons
                  iconName="chevron_right"
                  className={classNames('cursor-pointer size-4 transition-transform duration-200', {
                    'rotate-90': isOpen,
                  })}
                />
              )}
              <div className="w-full overflow-hidden overflow-ellipsis">{dimension ?? '(empty)'}</div>
            </div>
          </SkeletonLoader>
        </td>
        {yearMonths.map((yearMonth: string) => (
          <td
            key={yearMonth}
            className={classNames('text-right tabular-nums', {
              'bg-amber-50': isCurrentColumn(yearMonth),
            })}
            data-year-month={yearMonth}
          >
            <SkeletonLoader loading={loading}>{metrics[yearMonth]}</SkeletonLoader>
          </td>
        ))}
      </tr>
      {children &&
        (!expandable || isOpen) &&
        children.map(it => (
          <TableRow
            key={it.id}
            isCurrentColumn={isCurrentColumn}
            depth={depth + 1}
            data={data}
            expandable={expandable}
            expandedRows={expandedRows}
            item={it}
            loading={loading}
            yearMonths={yearMonths}
            onClickDimension={onClickDimension}
          />
        ))}
    </>
  );
};
